import { Component, OnInit } from '@angular/core';
import { SubSink } from 'subsink';
import { AutoUnsubscribe } from '../../../core/auto-unsubscribe';
import { RequestTypeEnum } from '../../../shared/xatena-api/request-type';
import { RequestTypeBaseComponent } from '../request-type-base/request-type-base.component';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { of } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { DemandRequestDetailService } from '../demand-request-detail.service';
import { RequestDto } from '../../../xatena-api';
import { FormlyModule } from '@ngx-formly/core';
import { AsyncPipe } from '@angular/common';

@AutoUnsubscribe()
@Component({
  selector: 'app-request-type-catalog-product',
  templateUrl: './request-type-catalog-product.component.html',
  styleUrls: ['./request-type-catalog-product.component.scss'],
  imports: [FormlyModule, AsyncPipe],
})
export class RequestTypeCatalogProductComponent
  extends RequestTypeBaseComponent
  implements OnInit
{
  constructor(
    protected translateService: TranslateService,
    demandRequestDetailService: DemandRequestDetailService
  ) {
    super(demandRequestDetailService);
  }

  subs = new SubSink();

  public get procurementTypeSelectList$() {
    const options: {
      key: number;
      value: string;
    }[] = [
      {
        key: 0,
        value: this.translateService.instant(
          'SHARED.PROCUREMENT_TYPE_SELECT.BUY'
        ),
      },
      {
        key: 1,
        value: this.translateService.instant(
          'SHARED.PROCUREMENT_TYPE_SELECT.CONSIGNMENT'
        ),
      },
      {
        key: 2,
        value: this.translateService.instant(
          'SHARED.PROCUREMENT_TYPE_SELECT.RENTAL'
        ),
      },
    ];

    return of(options);
  }

  public get reusableSelectList$() {
    const options: {
      key: boolean;
      value: string;
    }[] = [
      {
        key: true,
        value: this.translateService.instant('SHARED.REUSABLE_SELECT.YES'),
      },
      {
        key: false,
        value: this.translateService.instant('SHARED.REUSABLE_SELECT.NO'),
      },
    ];

    return of(options);
  }

  protected updateTotalCost(request: RequestDto) {
    const costTotal = +(request.costSingle ?? 0) * +(request.amount ?? 0);

    this.form.get('costTotal')?.setValue(costTotal, {
      emitEvent: false,
    });
  }

  ngOnInit(): void {
    this.subs.sink = this.form.valueChanges.subscribe(s => {
      this.updateTotalCost(s);
    });

    this.fields = [
      {
        hooks: {
          onInit: field => {
            this.subs.sink = this.isReadOnly$.subscribe(s => {
              if (s) {
                this.form.disable();
              } else {
                this.form.enable();
              }
            });
          },
        },
        hideExpression: model =>
          !(model.requestTypeId === RequestTypeEnum.CatalogProduct),
        fieldGroup: [
          {
            wrappers: ['mat-card'],
            templateOptions: {
              label: _('DEMAND_REQUEST.DETAIL.FORM.REQUEST_TYPE_6_GRP_1.LABEL'),
              translate: true,
            },
            fieldGroup: [
              {
                key: 'description',
                type: 'textarea',
                templateOptions: {
                  required: true,
                  appearance: this.formFieldAppearance,
                  label: _('DEMAND_REQUEST.DETAIL.FORM.DESCRIPTION.LABEL'),
                  description: _(
                    'DEMAND_REQUEST.DETAIL.FORM.DESCRIPTION.DESCRIPTION_RT6'
                  ),
                  translate: true,
                  autosize: true,
                  autosizeMinRows: 3,
                  autosizeMaxRows: 7,
                },
              },
              {
                key: 'reason',
                type: 'textarea',
                templateOptions: {
                  required: true,
                  appearance: this.formFieldAppearance,
                  label: _('DEMAND_REQUEST.DETAIL.FORM.REASON.LABEL'),

                  translate: true,
                  autosize: true,
                  autosizeMinRows: 3,
                  autosizeMaxRows: 7,
                },
              },
              {
                key: 'product',
                type: 'textarea',
                templateOptions: {
                  required: true,
                  appearance: this.formFieldAppearance,
                  label: _('DEMAND_REQUEST.DETAIL.FORM.PRODUCT.LABEL_RT6'),
                  description: _(
                    'DEMAND_REQUEST.DETAIL.FORM.PRODUCT.DESCRIPTION'
                  ),
                  translate: true,
                  autosize: true,
                  autosizeMinRows: 3,
                  autosizeMaxRows: 7,
                },
              },
              {
                key: 'costSingle',
                type: 'currency',
                templateOptions: {
                  required: true,
                  appearance: this.formFieldAppearance,
                  label: _('DEMAND_REQUEST.DETAIL.FORM.COST_SINGLE.LABEL'),
                  translate: true,
                  currency: this.currency$,
                },
              },
              {
                key: 'amount',
                type: 'input',
                templateOptions: {
                  type: 'number',
                  required: false,
                  appearance: this.formFieldAppearance,
                  label: _('DEMAND_REQUEST.DETAIL.FORM.AMOUNT.LABEL_RT6'),
                  translate: true,
                },
              },
              {
                key: 'costTotal',
                type: 'currency',
                templateOptions: {
                  required: true,
                  appearance: this.formFieldAppearance,
                  label: _('DEMAND_REQUEST.DETAIL.FORM.COST_TOTAL.LABEL'),
                  translate: true,
                  currency: this.currency$,
                },
              },
              {
                key: 'procurementType',
                type: 'select',
                templateOptions: {
                  required: false,
                  appearance: this.formFieldAppearance,
                  label: _('DEMAND_REQUEST.DETAIL.FORM.PROCUREMENT_TYPE.LABEL'),
                  translate: true,
                  options: this.procurementTypeSelectList$,
                  valueProp: 'key',
                  labelProp: 'value',
                },
              },
              {
                key: 'location',
                type: 'input',
                templateOptions: {
                  required: false,
                  appearance: this.formFieldAppearance,
                  label: _('DEMAND_REQUEST.DETAIL.FORM.LOCATION.LABEL'),
                  translate: true,
                },
              },
              {
                key: 'testDuration',
                type: 'input',
                templateOptions: {
                  required: false,
                  appearance: this.formFieldAppearance,
                  label: _('DEMAND_REQUEST.DETAIL.FORM.TEST_DURATION.LABEL'),
                  translate: true,
                },
              },
              {
                key: 'usageByOthers',
                type: 'input',
                templateOptions: {
                  required: false,
                  appearance: this.formFieldAppearance,
                  label: _('DEMAND_REQUEST.DETAIL.FORM.USAGE_BY_OTHERS.LABEL'),
                  translate: true,
                },
              },
              {
                key: 'reusable',
                type: 'select',
                templateOptions: {
                  required: false,
                  appearance: this.formFieldAppearance,
                  label: _('DEMAND_REQUEST.DETAIL.FORM.REUSABLE.LABEL'),
                  translate: true,
                  options: this.reusableSelectList$,
                  valueProp: 'key',
                  labelProp: 'value',
                },
              },
              {
                hideExpression: (model, formState, field) => {
                  return !model.reusable;
                },
                fieldGroup: [
                  {
                    key: 'reprocessingStatement',
                    type: 'checkbox',
                    templateOptions: {
                      required: false,
                      appearance: this.formFieldAppearance,
                      label: _(
                        'DEMAND_REQUEST.DETAIL.FORM.REPROCESSING_STATEMENT.LABEL'
                      ),
                      translate: true,
                    },
                  },
                  {
                    key: 'zsvaStatement',
                    type: 'checkbox',
                    templateOptions: {
                      required: false,
                      appearance: this.formFieldAppearance,
                      label: _(
                        'DEMAND_REQUEST.DETAIL.FORM.ZSVA_STATEMENT.LABEL'
                      ),
                      translate: true,
                    },
                  },
                ],
              },
              {
                key: 'hygieneStatement',
                type: 'checkbox',
                templateOptions: {
                  required: false,
                  appearance: this.formFieldAppearance,
                  label: _(
                    'DEMAND_REQUEST.DETAIL.FORM.HYGIENE_STATEMENT.LABEL'
                  ),
                  translate: true,
                },
              },
              {
                key: 'replacing',
                type: 'textarea',
                templateOptions: {
                  required: true,
                  appearance: this.formFieldAppearance,
                  label: _('DEMAND_REQUEST.DETAIL.FORM.REPLACING.LABEL_RT6'),
                  description: _(
                    'DEMAND_REQUEST.DETAIL.FORM.REPLACING.DESCRIPTION_RT6'
                  ),
                  translate: true,
                  autosize: true,
                  autosizeMinRows: 3,
                  autosizeMaxRows: 7,
                },
              },
            ],
          },
        ],
      },
    ];
  }
}
